import { Menu, SegmentedControl, Skeleton, Stack, Text, Title } from "@mantine/core";
import { IconMailForward } from "@tabler/icons-react";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Content } from "shared/components/global/Content";
import { FloatingActionButton } from "shared/components/global/FloatingActionButton/FloatingActionButton";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";
import { MessageDetailCard, MessageDetailCardAction } from "shared/components/message/MessageDetailCard";
import { MessageListSkeleton } from "shared/components/message/MessageListSkeleton";
import { useMessageActions } from "shared/components/message/useMessageActions";
import { permissionCheck, usePermissionCheck, usePermissions } from "shared/stores/oidc";
import { arr } from "shared/utils/array";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { NoMessagesFigure } from "src/assets/NoMessagesFigure";
import { graphql } from "src/gql";
import { MessageStatus, Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";
import { match } from "ts-pattern";

const Component = () => {
    const [filter, setFilter] = useState<MessageStatus | undefined>(MessageStatus.Published);
    const {
        data: { pages },
        isFetching,
        isRefetching,
    } = useSuspenseInfiniteQuery({ ...query(filter), refetchInterval: 5 * 1000 });
    const messages = pages.flat();

    const { permissions } = usePermissions();

    const canSendToGroup = usePermissionCheck(Permission.GroupMessageSendTo);
    const canSendToTopic = usePermissionCheck(Permission.TopicMessageSendTo);
    const canSend = canSendToGroup || canSendToTopic;

    const nav = useNavigate();
    const actions = useMessageActions();

    return (
        <Content>
            <Content.Heading>
                <Title order={3} size="h1">
                    Inbox
                </Title>
            </Content.Heading>

            <Content.Action>
                <div />
            </Content.Action>

            <Content.Action>
                <SegmentedControl
                    withItemsBorders={false}
                    color={"var(--mantine-primary-color-filled)"}
                    data={["Active", "Past", "All"]}
                    onChange={(val) =>
                        setFilter(
                            match(val)
                                .with("Active", () => MessageStatus.Published)
                                .with("Past", () => MessageStatus.Expired)
                                .otherwise(() => undefined),
                        )
                    }
                    styles={{ root: { backgroundColor: "var(--mantine-color-body)" } }}
                />
            </Content.Action>

            {!isFetching && messages.length == 0 && (
                <NoMessagesFigure>
                    <Text ta={"center"}>Looks like you don't have any messages yet!</Text>
                </NoMessagesFigure>
            )}

            {messages.map((message) => (
                <MessageDetailCard
                    key={message.id}
                    onClick={() => nav({ to: "/messages/$id", params: { id: message.id } })}
                    actions={arr
                        .of<MessageDetailCardAction>()
                        .appendIf(permissionCheck(permissions, Permission.GroupMessageSendFrom, message.sender.group.id), {
                            icon: null,
                            label: "Update Status",
                            onClick: () => actions.openUpdateStatusModal(message.id),
                            disabled: message.status != MessageStatus.Published,
                        })
                        .appendIf(canSend, {
                            label: "Forward",
                            icon: <IconMailForward size={16} />,
                            onClick: () => nav({ to: "/messages/$id/fwd", params: { id: message.id } }),
                        })
                        .take()}
                    unread
                    title={message.subject}
                    severity={message.severity}
                    status={message.status}
                    timestamp={new Date(Number.parseInt(message.publishedAt ?? ""))}
                    body={message.body}
                />
            ))}

            {isFetching && !isRefetching ? <MessageListSkeleton /> : null}

            <LoadMoreTrigger query={query(filter)} />

            {canSend && (
                <FloatingActionButton>
                    <Menu.Item component={Link} to="/messages/compose">
                        New Message
                    </Menu.Item>
                </FloatingActionButton>
            )}
        </Content>
    );
};

const query = (filter?: MessageStatus) =>
    createOffsetBasedGqlQueryOptions({
        variables: { filter },
        document: graphql(`
            query InboxMessages($limit: Int!, $offset: Int!, $filter: MessageStatus) {
                inbox(limit: $limit, offset: $offset, status: $filter) {
                    id
                    body
                    status
                    subject
                    publishedAt
                    sender {
                        group {
                            id
                        }
                    }
                    about {
                        didAck
                    }
                    severity
                }
            }
        `),
        queryKey: ["messages", "inbox", filter ?? "all"],
        select: ({ inbox }) => inbox,
    });

/** @public */
export const Route = createFileRoute("/_auth/messages/inbox")({
    component: Component,
    shouldReload: false,
    pendingComponent: () => (
        <Stack>
            {arr
                .make(10, () => (
                    <Skeleton>
                        <MessageDetailCard />
                    </Skeleton>
                ))
                .take()}
        </Stack>
    ),
    loader: () => queryClient.prefetchInfiniteQuery(query()),
});
